import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import { useApiErrorHandler } from '../../../useApiErrorHandler'

interface DeleteCategoryParams {
  id: string
}

const deleteCategory = async ({ id }: DeleteCategoryParams): Promise<void> => {
  await api.delete(`/category/${id}`)
}

export const useDeleteCategory = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const handleApiError = useApiErrorHandler()

  return useMutation<void, Error, DeleteCategoryParams>({
    mutationFn: deleteCategory,
    onSuccess: async (_, variables) => {
      showSuccessToast(t('toast.success'), t('toast.categoryDeleted'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['categories'] }),
        // Marked stale without asking again: the screen is still open and
        // a new request for the deleted item would answer « not found ».
        queryClient.invalidateQueries({ queryKey: ['category', variables.id], refetchType: 'none' }),
        queryClient.invalidateQueries({
          queryKey: ['category-contacts', { id: variables.id }],
          refetchType: 'none',
        }),
      ])
    },
    // A refused deletion says so instead of failing in silence.
    onError: handleApiError,
  })
}

